import { apiSlice, mockDataEnabled } from "./api.slice"
import {
  getDevBypassHeader,
  getLogoutUrl,
  getMiddlewareRoot,
} from "@/utils/helpers/configs"
import { setLoggedOut, setMemberId } from "@/store/member/member.slice"
import {
  BankingDetailsProductId,
  DEFERRED_PRODUCT_ID,
  GSS_PRODUCT_ID,
  GSU_PRODUCT_ID,
  investmentOptionsSortOrder,
  PERSIST_FORMS_SESSION_KEY,
  planNameSortOrder,
  productIds,
  RI_ACCOUNTS,
  RI_AP_TTR_PRODUCT_ID,
  RI_TAP_PRODUCT_ID,
  WSS_PRODUCT_ID,
} from "@/utils/helpers/mappings.js"
import { camelCase, flow, merge, pick } from "lodash"
import { createSelector } from "@reduxjs/toolkit"
import {
  resetInitialState,
  selectCurrentAccountIndex,
  setAccountIndex,
} from "@/store/ui/ui.slice"
import { formatQueryDate, parseLinkDate } from "@/utils/helpers/formatters"
import { memberHasOnlyGssAccounts } from "@/utils/helpers/personalDetailsBankingWidget"
import { differenceInYears } from "date-fns"
import { v4 as uuid } from "uuid"
import { currentDate, currentFinancialYear } from "@/utils/helpers/dateHelpers"
import {
  AccountBalance,
  AccountBalanceResp,
  AccountBalanceType,
  AnnualReviewsResp,
  Membership,
} from "@/types/Product"
import {
  MemberDetails,
  PensionProductIds,
  WssGsuProductIds,
} from "@/types/Member"
import { PendingTypeDefinition, ProductNumber } from "@/types/General"
import { RootState } from "@/store"
import {
  UseLazyQuery,
  UseQuery,
} from "@reduxjs/toolkit/dist/query/react/buildHooks"
import {
  BaseQueryFn,
  EndpointDefinitions,
  FetchBaseQueryError,
  QueryDefinition,
} from "@reduxjs/toolkit/query"
import {
  ApplicationData,
  ApplicationDataQuery,
  ApplicationDataResp,
  BeneficiaryResp,
  CreateStoredDocumentQuery,
  CreateStoredDocumentResp,
  LinkResourceResponse,
  LinkResponse,
  PensionResp,
  StoredDocumentsResp,
  SubmitNoticeOfRetirementBody,
  SubmitNoticeOfRetirementResp,
  UpdateApplicationDataBody,
  UpdateApplicationDataResp,
} from "@/types/LinkApi"
import {
  InsuranceProductIds,
  InsuranceRateResp,
  InsuranceRatesQuery,
  InsuranceRatesResponse,
  InsuranceResp,
  InsuranceSpecialField,
  InsuranceSpecialFieldsResp,
  InsuranceVariationPayload,
} from "@/types/Insurance"
import {
  ApplicationPensionPayload,
  RolloverFundResponse,
  RolloversResponse,
} from "@/types/Workflow"
import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes"
import {
  InvestmentsResp,
  InvestmentSwitchResp,
  InvestmentSwitchRiResp,
} from "@/types/Investment"
import { AddressSearchResp, CountryResponse } from "@/types/Address"
import { MoneyInOutResp, TransactionsResp } from "@/types/Transactions"
import {
  AddBankAccountPayload,
  AddBankAccountResp,
  BankAccountReceiptEmailPayload,
  BankAccountReceiptEmailType,
  ChangeBankAccountPayload,
  ChangeBankAccountResp,
  CloseBankAccountPayload,
  CloseBankAccountResp,
  MemberBankAccountsResp,
} from "@/types/BankAccount"
import { IlbaInclusionResponse, IlbaReceiptRequest } from "@/types/Ilba"
import { PensionVariationPayload } from "@/components/EditPensionPaymentsForm/editPensionPaymentBody"
import {
  isAllowedProductNumber,
  productAccountBalancesUrl,
} from "@/store/apis/linkApiUrls"

const apiRoot = getMiddlewareRoot()

// majority of link api queries use productId / accountNumber
// in the query url and provides Member tags for cache invalidation
const queryBase = (key: string, extras = {}): any => ({
  query: (arg: any) => {
    // just the product id passed as arg
    if (!isNaN(+arg)) {
      return `${apiRoot}/product/${arg}/${key}`
    }
    // obj with the product id and remaining query items
    const { id, ...query } = arg
    return `${apiRoot}/product/${id}/${key}?${new URLSearchParams(query)}`
  },
  providesTags: ["Member"],
  ...extras,
})

// The RI/TTR account balances aren't split by accounts
// but their investment options are, so we duplicate them
// and include an accountNumber in the duplicate which we
// can associate with the memberships api response
export function splitByInvestments(
  resp: AccountBalanceResp[]
): AccountBalanceResp[] {
  return resp.reduce((carry: AccountBalanceResp[], item) => {
    if ([RI_TAP_PRODUCT_ID, RI_AP_TTR_PRODUCT_ID].includes(item.productId)) {
      const accountNumbers = item.investmentOption.map(
        (option) => option.accountNumber
      )
      const accounts = [...new Set(accountNumbers)]
        .map((accountNumber) => ({
          ...item,
          accountNumber,
        }))
        .map((account) => {
          const investmentOption = account.investmentOption.filter(
            (option) => option.accountNumber === account.accountNumber
          )
          return {
            ...account,
            investmentOption,
            amount: investmentOption.reduce((acc, option) => {
              return acc + option.amount
            }, 0),
          }
        })
      return [...carry, ...accounts]
    }
    return [...carry, item]
  }, [])
}

const sortPlanNames = (
  { planName: planA }: Pick<AccountBalance, "planName">,
  { planName: planB }: Pick<AccountBalance, "planName">
) =>
  planNameSortOrder.indexOf(planA as (typeof planNameSortOrder)[number]) >
  planNameSortOrder.indexOf(planB as (typeof planNameSortOrder)[number])
    ? 1
    : -1

type TransformBalance = AccountBalanceResp | AccountBalanceResp[]

// The response from the account balances api requires
// a lot of transformation to be fully useful across contexts
export function transformAccountBalanceResponse(
  item: TransformBalance
): AccountBalance[] | AccountBalance {
  // can be applied to an array of balance
  // responses which will also sort them
  if (Array.isArray(item)) {
    return item
      .map((item2) => transformAccountBalanceResponse(item2) as AccountBalance)
      .sort(sortPlanNames)
  }

  // the only way to determine the difference between RI and TTR
  // is to check if any of the investments items end in TTR
  const addPlanNameAndTTR = (item: AccountBalanceResp) => {
    const types =
      productIds[item.productId as keyof typeof productIds].split("|")
    if (
      item.investmentOption.find((option) => /TTR$/.test(option.unitName)) &&
      types.length === 2
    ) {
      return { ...item, planName: types[1], isTTR: true }
    }

    return { ...item, planName: types[0], isTTR: false }
  }

  // a lot of matrix data relies on these codes which doesn't exist
  // in the link data so just inject the codes early to help mapping
  const addMatrixCode = (
    item: AccountBalanceResp & Pick<AccountBalance, "isTTR">
  ) => {
    switch (item.productId) {
      case GSU_PRODUCT_ID:
        return { ...item, productCode: "GSU" }
      case WSS_PRODUCT_ID:
        return { ...item, productCode: "WSS" }
      case RI_TAP_PRODUCT_ID:
        return { ...item, productCode: "TAP" }
      case RI_AP_TTR_PRODUCT_ID:
        return { ...item, productCode: item.isTTR ? "TTR" : "RI" }
      case GSS_PRODUCT_ID:
        return { ...item, productCode: "GSS" }
      case DEFERRED_PRODUCT_ID:
        return { ...item, productCode: "Deferred" }
    }
  }

  // react likes objects identifiable by uids
  const addUid = (item: AccountBalanceResp) => ({ ...item, uid: uuid() })

  return flow([addPlanNameAndTTR, addMatrixCode, addUid])(
    item
  ) as AccountBalance
}

/**
 * Pass in true to also kill the Matrix session
 */
type LogoutProps = true | void

interface QueryProduct {
  id: number
  account?: string | null
}

function isNumber(val: any): val is number {
  return !isNaN(+val)
}

type PayloadBody<T> = {
  body: T
}

const linkApi = apiSlice.injectEndpoints({
  endpoints: (build) => {
    // simple endpoints that just accept a product id
    const productMapped = [
      "transactions",
      "annual-reviews",
      "contribution-totals",
      "insurances",
      "special-fields",
      "money-in-out",
      "balance-details",
      "mfa-list",
      "suppressions",
      "bpay-refs",
      "rollovers",
      "variances",
    ].reduce<EndpointDefinitions>((carry, key) => {
      const name = camelCase(key)
      carry[name] = build.query(queryBase(key))
      return carry
    }, {})

    // batched request endpoints that can take either an array
    // of productIds or array of accounts and returns all the queries
    const batchedProductMapped = [
      "beneficiaries",
      "service-start-date",
      "pension",
    ].reduce<EndpointDefinitions>((carry, key) => {
      const name = camelCase(key)
      carry[name] = build.query<
        any,
        (Pick<AccountBalance, "accountNumber" | "productId"> | ProductNumber)[]
      >({
        async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
          const results = await Promise.all<PendingTypeDefinition>(
            args.map((arg) => {
              // just the product id passed as arg
              if (isNumber(arg)) {
                if (!isAllowedProductNumber(arg as ProductNumber)) {
                  throw new Error(
                    "Refusing request: product id is not allowlisted"
                  )
                }
                return fetchWithBQ(`${apiRoot}/product/${arg}/${key}`)
              }

              const { productId, accountNumber } = arg
              if (!isAllowedProductNumber(productId)) {
                throw new Error(
                  "Refusing request: product id is not allowlisted"
                )
              }
              return fetchWithBQ(
                `${apiRoot}/product/${productId}/${key}${
                  accountNumber ? `?accountNumber=${accountNumber}` : ""
                }`
              )
            })
          )

          const hasError = results.find((result) => result?.error)
          if (hasError) {
            return { error: hasError.error }
          }

          return {
            data: args.map((arg, index) => {
              const result = results[index]?.data
              if (isNumber(arg)) {
                return {
                  productId: arg,
                  ...result,
                }
              }
              return {
                ...pick(arg, "productId", "accountNumber"),
                ...result,
              }
            }),
          }
        },
        providesTags: ["Member"],
      })
      return carry
    }, {})

    return merge({}, productMapped, batchedProductMapped, {
      memberDetails: build.query<MemberDetails, void>({
        query: () => `${apiRoot}/member`,
        providesTags: ["Member", "MemberDetails"],
        // RI-only: drop duplicate null accountNumber rows; WSS/GSS/deferred legitimately have no account number
        transformResponse: (resp: MemberDetails) => ({
          ...resp,
          memberships: (resp.memberships ?? []).filter(
            (membership) =>
              membership.accountNumber != null ||
              !RI_ACCOUNTS.includes(
                membership.productNumber as (typeof RI_ACCOUNTS)[number]
              )
          ),
        }),
      }),
      accountBalances: build.query<AccountBalance[], void>({
        // batched response of all accnt balances for current FY
        // called very early in the app so can be queried
        // via selectors made available by exports below
        query: () => {
          const { endDate, isRolledBack } = currentFinancialYear()
          if (isRolledBack) {
            return `${apiRoot}/balances?effectiveDate=${formatQueryDate(
              endDate
            )}`
          }
          return `${apiRoot}/balances`
        },
        async onQueryStarted(_arg, { queryFulfilled, dispatch, getState }) {
          await queryFulfilled
          const state = getState() as RootState
          const current = selectCurrentAccountIndex(state)
          const accounts = selectAccountBalances(state)
          if (!accounts[current]) {
            console.warn("Current account index outside of memberships range")
            dispatch(setAccountIndex(0))
          }
        },
        transformResponse: (resp: AccountBalanceResp[]) => {
          const split = splitByInvestments(resp)
          return transformAccountBalanceResponse(split) as AccountBalance[]
        },
        providesTags: ["Member"],
      }),
      accountBalanceDate: build.query<
        AccountBalance[],
        { date: string; id: ProductNumber }
      >({
        query: ({ id, date }) => productAccountBalancesUrl(id, date),
        transformResponse: (resp: AccountBalanceResp) => {
          const split = splitByInvestments([resp])
          return transformAccountBalanceResponse(split) as AccountBalance[]
        },
        providesTags: ["Member"],
      }),
      accountBalanceDates: build.query<
        AccountBalance[],
        { dates: string[]; id: ProductNumber }
      >({
        async queryFn({ dates, id }, _api, _extra, fetchWithBQ) {
          if (!isAllowedProductNumber(id)) {
            return {
              error: {
                status: 400,
                data: "Invalid product id",
              } as FetchBaseQueryError,
            }
          }

          const results = await Promise.all(
            dates.map((date) =>
              fetchWithBQ(productAccountBalancesUrl(id, date))
            )
          )

          const hasError = results.find((result) => result?.error)
          if (hasError) {
            return { error: hasError.error as FetchBaseQueryError }
          }

          return {
            data: results.map((result) =>
              flow(
                splitByInvestments,
                transformAccountBalanceResponse
              )([result.data as AccountBalance])
            ) as AccountBalance[],
          }
        },
        providesTags: ["Member"],
      }),
      getMemberId: build.query<{ memberId: string }, void>({
        query: () => `${apiRoot}/member-id`,
        // on success store the currently set session
        // memberid in redux as the mocked id and causes
        // the mount/remount which rtk can then start
        // filling the request caches
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          const { data } = await queryFulfilled
          dispatch(setMemberId(data?.memberId))
        },
        providesTags: ["Member"],
      }),
      setMemberId: build.mutation<{ updated: boolean }, string | null>({
        query: (memberId) => ({
          url: `${apiRoot}/member-id`,
          method: "POST",
          body: {
            memberId,
          },
        }),
        async onQueryStarted(_arg, { dispatch }) {
          // we need to clear the existing member id so that any mounted
          // components don't trigger another request as soon as we
          // invalidate the current cache. By clearing the id we
          // unmount the current components which rtk then unsubscribes
          dispatch(setMemberId("pending"))
          // reset to the initial state
          // ui state on member id change
          dispatch(resetInitialState())
          // also clear any persist forms from the session storage
          // this should reinstate on next form mount
          window.sessionStorage.removeItem(PERSIST_FORMS_SESSION_KEY)
        },
        invalidatesTags: ["Member", "Switches"],
      }),
      investments: build.query<InvestmentsResp["options"], ProductNumber>(
        queryBase("investments", {
          transformResponse: (resp: InvestmentsResp) =>
            resp.options.sort((a, b) =>
              investmentOptionsSortOrder.indexOf(
                a.unitName as (typeof investmentOptionsSortOrder)[number]
              ) >
              investmentOptionsSortOrder.indexOf(
                b.unitName as (typeof investmentOptionsSortOrder)[number]
              )
                ? 1
                : -1
            ),
        })
      ),
      logout: build.query<void, LogoutProps>({
        query: () => `${apiRoot}/logout`,
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          dispatch(setLoggedOut("pending"))
          try {
            await queryFulfilled
          } catch {
            // Even if logout API call fails, proceed with cleanup and redirect
          }

          // set logged out to true to trigger the logout component to show
          dispatch(setLoggedOut(true))

          // clear persist forms
          window.sessionStorage.removeItem(PERSIST_FORMS_SESSION_KEY)
          // clear matrix data to prevent re-login with stale HTML confi

          // always kill matrix session when logging out
          window.location.href = getLogoutUrl()
        },
      }),
      logoutSync: build.query<boolean, void>({
        queryFn: async (_args, _queryApi, _extraOptions, fetchWithBQ) => {
          await Promise.all([
            fetchWithBQ(`${apiRoot}/logout`),
            fetchWithBQ(getLogoutUrl()),
          ])

          // clear persist forms
          window.sessionStorage.removeItem(PERSIST_FORMS_SESSION_KEY)

          return { data: true }
        },
      }),
      investmentSwitches: build.query<
        InvestmentSwitchResp | InvestmentSwitchRiResp,
        QueryProduct
      >({
        query: ({ id, account }) =>
          `${apiRoot}/product/${id}/` +
          ([RI_TAP_PRODUCT_ID, RI_AP_TTR_PRODUCT_ID].includes(id)
            ? `investment-switches-ri?accountNumber=${account}`
            : "investment-switches"),
        providesTags: (_result, _error, { id, account }) => [
          { type: "Switches", id: account ?? id },
          "Switches",
        ],
      }),
      investmentSwitch: build.mutation<
        { resource: { receiptNo: string } },
        QueryProduct & { body: PendingTypeDefinition }
      >({
        query: ({ id, body, account }) => ({
          url:
            `${apiRoot}/product/${id}/` +
            ([RI_TAP_PRODUCT_ID, RI_AP_TTR_PRODUCT_ID].includes(id)
              ? `investment-switches-ri?accountNumber=${account}`
              : "investment-switches"),

          body,
          method: "POST",
        }),
        invalidatesTags: (_result, _error, { id, account }) => [
          { type: "Switches", id: account ?? id },
        ],
      }),
      cancelInvestmentSwitch: build.mutation<
        void,
        QueryProduct & { skipInvalidate?: boolean }
      >({
        query: ({ id, account }) => ({
          url:
            `${apiRoot}/product/${id}/` +
            ([RI_TAP_PRODUCT_ID, RI_AP_TTR_PRODUCT_ID].includes(id)
              ? `investment-switches-ri?accountNumber=${account}`
              : "investment-switches"),
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { skipInvalidate, account, id }) =>
          skipInvalidate === true
            ? []
            : [{ type: "Switches", id: account ?? id }],
      }),
      insuranceSpecialFields: build.query<
        InsuranceSpecialField[][],
        {
          id: Exclude<InsuranceProductIds, typeof GSS_PRODUCT_ID>
          allPolicyIds: number[]
        }
      >({
        async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
          const { id, allPolicyIds } = args
          const results = await Promise.all(
            allPolicyIds.map((policy) => {
              return fetchWithBQ(
                `${apiRoot}/product/${id}/insurance-special-fields?policyId=${policy}`
              ) as QueryReturnValue<InsuranceSpecialFieldsResp>
            })
          )
          const allSpecialFields = results.map((item) => {
            return item.data?.resource.specialFields ?? []
          })

          return { data: allSpecialFields }
        },
        providesTags: ["Member"],
      }),
      insuranceRates: build.query<
        InsuranceRatesResponse,
        {
          id: number
          allQueries: InsuranceRatesQuery[]
        }
      >({
        async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
          const { id, allQueries } = args
          const results = await Promise.all(
            allQueries.map((query) => {
              const { fetch, params } = query
              return fetch
                ? (fetchWithBQ(
                    `${apiRoot}/product/${id}/insurance-rates?${new URLSearchParams(
                      params
                    )}`
                  ) as QueryReturnValue<InsuranceRateResp>)
                : { data: undefined }
            })
          )

          return {
            data: results.reduce((carry, result, i) => {
              const key = allQueries[i].key
              return {
                ...carry,
                [key]: result.data?.resource?.rate ?? 0,
              }
            }, {}) as InsuranceRatesResponse,
          }
        },
        providesTags: ["Member"],
      }),
      insuranceVariations: build.mutation<
        LinkResponse<{ receiptNo: number }>,
        {
          id: Exclude<InsuranceProductIds, typeof GSS_PRODUCT_ID>
          body: InsuranceVariationPayload
        }
      >({
        query: ({ id, body }) => ({
          url: `${apiRoot}/product/${id}/insurance-variations`,
          body,
          method: "POST",
        }),
      }),
      pensionVariation: build.mutation<
        LinkResponse<{
          receiptNo?: string | number
          applicationId?: string | number
        }>,
        {
          productId: ProductNumber
          accountNumber: string
          body: Partial<PensionVariationPayload> &
            Pick<PensionVariationPayload, "changeStartDate">
        }
      >({
        query: ({ productId, accountNumber, body }) => ({
          url: `${apiRoot}/product/${productId}/pension-variation?${new URLSearchParams({ accountNumber })}`,
          method: "POST",
          body,
        }),
      }),
      insuranceProfessional: build.mutation({
        query: ({ id, body }) => ({
          url: `${apiRoot}/product/${id}/insurance-professional`,
          body,
          method: "POST",
        }),
      }),
      aiaSaml: build.query({
        query: (id) => ({
          url: `${apiRoot}/product/${id}/aia-saml`,
        }),
        providesTags: ["Member"],
      }),
      insuranceProtect: build.mutation({
        query: ({ id, body }) => ({
          url: `${apiRoot}/product/${id}/insurance-protect`,
          body,
          method: "PUT",
        }),
        // invalidate IlbaPreference tag upon mutation to refresh ILBA eligibility states
        invalidatesTags: ["IlbaPreference" as any],
      }), // this is submit
      submitInsuranceProtect: build.mutation({
        query: (body) => ({
          url: `${apiRoot}/protect-your-super`,
          body,
          method: "POST",
        }),
      }), // sends an email
      submitIlbaReceipt: build.mutation<void, IlbaReceiptRequest>({
        query: (body) => ({
          url: `${apiRoot}/ilba-receipt`,
          body,
          method: "POST",
        }),
      }), // sends ILBA confirmation email
      workflowFunds: build.query<RolloverFundResponse, { query: string }>({
        query: ({ query }) => `${apiRoot}/funds?search=${query}`,
        providesTags: ["Member"],
      }),
      rolloversSwitch: build.mutation({
        query: ({ id, body }) => ({
          url: `${apiRoot}/product/${id}/rollovers`,
          body,
          method: "POST",
        }),
      }),
      memberDocuments: build.query({
        query: () => `${apiRoot}/member/documents`,
        providesTags: ["Member"],
      }),
      commsPrefs: build.query({
        query: () => `${apiRoot}/member/comms-prefs`,
        providesTags: ["Member", "CommsPrefs"],
      }),
      updateCommsPrefs: build.mutation({
        query: (body) => ({
          url: `${apiRoot}/member/comms-prefs`,
          body,
          method: "PUT",
        }),
        invalidatesTags: ["CommsPrefs"],
      }),
      updateDetails: build.mutation<
        void,
        PayloadBody<{ member: Partial<MemberDetails["member"]> }>
      >({
        query: ({ body }) => ({
          url: `${apiRoot}/member/details`,
          body,
          method: "PUT",
        }),
        invalidatesTags: ["MemberDetails", "UserProfile"],
      }),
      countries: build.query<CountryResponse, void>({
        query: () => `${apiRoot}/countries`,
      }),
      addressSearch: build.query<AddressSearchResp, string>({
        query: (query) => `${apiRoot}/address?search=${query}`,
      }),
      submitFeedback: build.mutation({
        query: (body) => ({
          url: `${apiRoot}/member/feedback`,
          body,
          method: "POST",
        }),
      }),
      submitNoticeOfRetirement: build.mutation<
        SubmitNoticeOfRetirementResp,
        SubmitNoticeOfRetirementBody
      >({
        query: (body) => ({
          url: `${apiRoot}/member/notification-of-retirement`,
          body,
          method: "POST",
        }),
      }),
      insurancesBatched: build.query<
        (InsuranceResp & { productId: InsuranceProductIds })[],
        InsuranceProductIds[]
      >({
        queryFn: async (ids, _api, _options, baseQuery) => {
          const results = await Promise.all(
            ids.map((id) => baseQuery(`${apiRoot}/product/${id}/insurances`))
          )

          const hasError = results.find((resp) => resp.error)

          if (hasError) {
            return { error: hasError.error as FetchBaseQueryError }
          }

          return {
            data: results.map((resp, index) => ({
              productId: ids[index],
              insurances: (resp.data as InsuranceResp).insurances,
            })),
          }
        },
        providesTags: ["Member"],
      }),
      submitContribution: build.mutation({
        query: (body) => ({
          url: `${apiRoot}/payroll-deduction`,
          body,
          method: "POST",
        }),
      }),
      removeApplicationData: build.mutation<
        void,
        Required<Pick<UpdateApplicationDataBody, "applicationDataId">>
      >({
        query: ({ applicationDataId }) => ({
          url: `${apiRoot}/member/application-data?id=${applicationDataId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["ApplicationData"],
      }),
      updateApplicationData: build.mutation<
        UpdateApplicationDataResp,
        UpdateApplicationDataBody
      >({
        query: (body) => ({
          url: body.applicationDataId
            ? `${apiRoot}/member/application-data?id=${body.applicationDataId}`
            : `${apiRoot}/member/application-data`,
          body: { applicationData: body },
          method: "POST",
        }),
        invalidatesTags: ["ApplicationData"],
      }),
      applicationData: build.query<
        ApplicationDataResp,
        ApplicationDataQuery | void
      >({
        query: () => `${apiRoot}/member/application-data`,
        providesTags: ["Member", "ApplicationData"],
      }),
      applicationDataById: build.query<
        LinkResourceResponse<{ applicationData: ApplicationData }>,
        { applicationDataId: string }
      >({
        query: ({ applicationDataId }) =>
          `${apiRoot}/member/application-data?id=${applicationDataId}`,
        providesTags: ["Member", "ApplicationData"],
      }),
      storedDocuments: build.query<StoredDocumentsResp, void>({
        query: () => `${apiRoot}/member/stored-documents`,
        providesTags: ["Member", "StoredDocuments"],
      }),
      createStoredDocument: build.mutation<
        CreateStoredDocumentResp,
        CreateStoredDocumentQuery
      >({
        async queryFn(arg) {
          // MUFG multipart fields: DocumentType (extension), DocumentName (sanitised, no extension), MultipartFile
          const extension = arg.file.name.split(".").pop()?.toUpperCase() ?? ""
          const documentName = arg.file.name
            .replace(/\.[^.]+$/, "")
            .replace(/[^A-Za-z0-9 _-]/g, "_")
            .slice(0, 256)

          const formData = new FormData()
          formData.append("DocumentType", extension)
          formData.append("DocumentName", documentName)
          formData.append("MultipartFile", arg.file)

          // Use native fetch — fetchBaseQuery JSON-serializes FormData bodies, breaking multipart
          try {
            const headers: HeadersInit = {}
            const devBypass = getDevBypassHeader()
            if (devBypass) headers["X-Dev-Bypass"] = String(devBypass)

            const response = await fetch(`${apiRoot}/member/stored-documents`, {
              method: "POST",
              body: formData,
              credentials: "include",
              headers,
            })

            if (!response.ok) {
              return {
                error: {
                  status: response.status,
                  data: await response.json().catch(() => null),
                } as FetchBaseQueryError,
              }
            }

            return { data: (await response.json()) as CreateStoredDocumentResp }
          } catch (e) {
            return {
              error: {
                status: "FETCH_ERROR",
                error: String(e),
              } as FetchBaseQueryError,
            }
          }
        },
        invalidatesTags: ["StoredDocuments"],
      }),
      removeStoredDocument: build.mutation<null, string>({
        query: (id) => ({
          url: `${apiRoot}/member/stored-documents?id=${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["StoredDocuments"],
      }),
      applicationPension: build.mutation<
        LinkResponse<{ applicationId: string }>,
        ApplicationPensionPayload
      >({
        query: (body) => ({
          url: `${apiRoot}/member/application-pension`,
          method: "POST",
          body,
        }),
      }),
      // GET is product-scoped: /product/{id}/banking-details-get -> ProductApi
      // -> .../v1/products/{productId}/members/{memberId}/bankaccounts.
      // Link only allows products 30/40/50/70; GSS 10/15 is rejected.
      // accountNumber is required when the product has multiple memberships
      // (e.g. several RI accounts under product 30) or ProductApi throws ambiguous.
      memberBankAccounts: build.query<
        LinkResourceResponse<MemberBankAccountsResp>,
        {
          productId: BankingDetailsProductId
          accountNumber?: string | null
        }
      >({
        query: ({ productId, accountNumber }) => {
          const params = accountNumber
            ? `?${new URLSearchParams({ accountNumber })}`
            : ""
          return `${apiRoot}/product/${productId}/banking-details-get${params}`
        },
        // BankAccounts only — balances/member profile are unrelated to on-file bank list
        providesTags: ["BankAccounts"],
      }),
      // POST is member-scoped (no id): /member/banking-details -> MemberApi ->
      // .../v1/clients/{clientNo}/bankaccounts; invalidates BankAccounts so the widget
      // refetches without bouncing NavLoader (which keys off accountBalances isFetching)
      addBankAccount: build.mutation<AddBankAccountResp, AddBankAccountPayload>(
        {
          query: (body) => ({
            url: `${apiRoot}/member/banking-details-add`,
            method: "POST",
            body,
          }),
          invalidatesTags: ["BankAccounts"],
        }
      ),
      sendBankAccountReceipt: build.mutation<
        void,
        {
          type: BankAccountReceiptEmailType
          body: BankAccountReceiptEmailPayload
        }
      >({
        query: ({ type, body }) => ({
          url: `${apiRoot}/member/bank-account-receipt?type=${type}`,
          method: "POST",
          body,
        }),
      }),
      // POST is member-scoped (no id): /member/banking-details-close?bankAccountId=
      // -> MemberApi -> .../v1/clients/{clientNo}/bankaccounts/{bankAccountId}/close;
      // bank details in the body confirm the account being closed
      closeBankAccount: build.mutation<
        CloseBankAccountResp,
        {
          bankAccountId: string
          body: CloseBankAccountPayload
        }
      >({
        query: ({ bankAccountId, body }) => ({
          url: `${apiRoot}/member/banking-details-close?bankAccountId=${bankAccountId}`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["BankAccounts"],
      }),
      // POST is member-scoped (no id): /member/banking-details-change?bankAccountId=
      // -> MemberApi -> .../v1/clients/{clientNo}/bankaccounts/{bankAccountId}/change;
      // one call per RI pension account reassigned to this bank account
      changeBankAccount: build.mutation<
        ChangeBankAccountResp,
        {
          bankAccountId: string
          body: ChangeBankAccountPayload
        }
      >({
        query: ({ bankAccountId, body }) => ({
          url: `${apiRoot}/member/banking-details-change?bankAccountId=${bankAccountId}`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["BankAccounts"],
      }),

      getIlbaInclusion: build.query<
        IlbaInclusionResponse,
        { memberId: string; productId: string }
      >({
        query: ({ productId }) => ({
          url: `${apiRoot}/product/${productId}/ilba`,
          method: "GET",
        }),
        providesTags: ["IlbaPreference" as any],
      }),
    })
  },
})

// inject mocking apis for development
if (process.env.NODE_ENV === "development" && mockDataEnabled) {
  // using require avoid webpack including the mockjs
  linkApi.injectEndpoints(require("./mockApis")["mockLinkApis"])
}

// Selectors
const memberDetails = linkApi.endpoints.memberDetails.select(undefined)

export const selectMemberDetails = createSelector(memberDetails, (result) => {
  // let TS know we're gonna crash regardless if this is missing
  if (!result.data?.member) {
    // Error (not string) so ErrorBoundary reports message/stack in UAT/prod
    throw new Error("Missing member details")
  }
  return result.data.member
})

export const selectMembersAge = createSelector(selectMemberDetails, (member) =>
  differenceInYears(currentDate(), parseLinkDate(member.dateOfBirth))
)

// WARNING: memberships won't distinguish TTR so its often better to use the
// selectAccountBalances to determine the products a member has
export const selectMemberships = createSelector(
  memberDetails,
  (result) => result.data?.memberships as Membership[] // assume memberships will always exist
)

export const selectMembershipById = createSelector(
  selectMemberships,
  (
    _state: RootState,
    account: Pick<AccountBalance, "productId" | "accountNumber">
  ) => account,
  (acc, account) => {
    if (!acc) return undefined

    const exact = acc.find(
      (item) =>
        item.productNumber === account.productId &&
        item.accountNumber === (account.accountNumber ?? null)
    )
    if (exact) return exact

    // non-RI balances (e.g. WSS, deferred) have no accountNumber on the balance row
    if (account.accountNumber == null) {
      return acc.find((item) => item.productNumber === account.productId)
    }

    return undefined
  }
)

export const selectProductNumbers = createSelector(
  selectMemberships,
  (result) => [...new Set(result?.map((item) => item.productNumber))]
)

const accountBalances = linkApi.endpoints.accountBalances.select(undefined)
export const selectAccountBalances = createSelector(
  accountBalances,
  (result) => result?.data ?? []
)

export const selectActiveAccount = createSelector(
  selectAccountBalances,
  selectCurrentAccountIndex,
  (arr, index) => arr?.[index]
)

export const selectAccountById = createSelector(
  selectAccountBalances,
  (
    _state: RootState,
    account: ProductNumber | Pick<AccountBalance, "productId" | "accountNumber">
  ) => account,
  (acc, account) => {
    return acc.find(({ productId, accountNumber }) =>
      typeof account === "number"
        ? productId === account
        : productId === account.productId &&
          accountNumber === account.accountNumber
    ) as AccountBalance
  }
)

export const selectAccountsByIds = createSelector(
  selectAccountBalances,
  (
    _state: RootState,
    accounts:
      | Pick<AccountBalance, "productId" | "accountNumber">[]
      | readonly ProductNumber[]
  ) => accounts,
  (acc, accounts) =>
    acc.filter((account) =>
      accounts.some((value) =>
        typeof value === "number"
          ? account.productId === value
          : account.productId === value.productId &&
            account.accountNumber === value.accountNumber
      )
    )
)

/**
 * Has ANY type of RI account (RIAP,TTR and TAP)
 */
export const selectHasRi = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => RI_ACCOUNTS.includes(item.productId))
)

export const selectHasTap = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === RI_TAP_PRODUCT_ID)
)

export const selectHasApOrTtr = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === RI_AP_TTR_PRODUCT_ID)
)

export const selectHasRiAp = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === RI_AP_TTR_PRODUCT_ID && !item.isTTR)
)

export const selectHasTtr = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === RI_AP_TTR_PRODUCT_ID && item.isTTR)
)

export const selectHasWss = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === WSS_PRODUCT_ID)
)

export const selectHasGsu = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === GSU_PRODUCT_ID)
)

export const selectHasGss = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === GSS_PRODUCT_ID)
)

export const selectHasDeferred = createSelector(selectAccountBalances, (arr) =>
  arr.some((item) => item.productId === DEFERRED_PRODUCT_ID)
)

/** GESB-631 — hide Personal Details banking widget when every linked product is GSS. */
export const selectIsGssOnlyMember = createSelector(
  selectAccountBalances,
  memberHasOnlyGssAccounts
)

export const selectActiveRi = createSelector(selectActiveAccount, (acc) =>
  [RI_TAP_PRODUCT_ID, RI_AP_TTR_PRODUCT_ID].includes(acc.productId)
)

// Active tab is RI Allocated Pension (not TTR / Term Allocated) — gates one-off withdrawal CTA
export const selectActiveRiAp = createSelector(
  selectActiveAccount,
  (acc) => acc.productId === RI_AP_TTR_PRODUCT_ID && !acc.isTTR
)

export const selectHasMultipleRiAccounts = createSelector(
  selectAccountBalances,
  (arr) => arr.filter((item) => RI_ACCOUNTS.includes(item.productId)).length > 1
)

export const selectHasProductId = createSelector(
  selectProductNumbers,
  (_state: RootState, ids: ProductNumber[]) => ids,
  (current, ids) => current.some((id) => ids.includes(id))
)

export const selectSuppressClosingBalance = createSelector(
  selectProductNumbers,
  (_state: RootState, id: ProductNumber) => id,
  (ids, current) => {
    // if member has both products and the id passed is one of them
    const products = [GSS_PRODUCT_ID, DEFERRED_PRODUCT_ID] as const
    return (
      products.every((id) => ids.includes(id)) &&
      products.includes(current as (typeof products)[number])
    )
  }
)

type BaseQueryDefinition<Query, Response> = QueryDefinition<
  Query,
  BaseQueryFn,
  string,
  Response
>

type InsuranceQuery = BaseQueryDefinition<InsuranceProductIds, InsuranceResp>

type BeneficiaryQuery = BaseQueryDefinition<
  ProductNumber[],
  (BeneficiaryResp & Pick<AccountBalance, "productId">)[]
>

type PensionQuery = BaseQueryDefinition<
  AccountBalanceType<PensionProductIds>[],
  (PensionResp & Pick<AccountBalance, "productId" | "accountNumber">)[]
>

type RolloversQuery = BaseQueryDefinition<WssGsuProductIds, RolloversResponse>

type MoneyInOutQuery = BaseQueryDefinition<
  {
    id: ProductNumber
    startDate: string
    endDate: string
    includeBuckets: boolean
  },
  MoneyInOutResp
>

type AnnualReviewsQuery = BaseQueryDefinition<
  { id: ProductNumber; reviewDate?: string },
  AnnualReviewsResp
>

type TransactionsQuery = BaseQueryDefinition<
  {
    id: ProductNumber
    accountNumber?: string
    startDate: string
    endDate: string
  },
  TransactionsResp
>

// productId + accountNumber disambiguate ProductApi membership resolution;
// bank account list itself is still member-wide
type MemberBankAccountsQuery = BaseQueryDefinition<
  {
    productId: BankingDetailsProductId
    accountNumber?: string | null
  },
  LinkResourceResponse<MemberBankAccountsResp>
>

/**
 * Typescript cant infer anything from the dynamically
 * generated endpoints so just tell it they exist for now
 */
export type LinkApi = typeof linkApi & {
  useTransactionsQuery: UseQuery<TransactionsQuery>
  useLazyTransactionsQuery: UseLazyQuery<TransactionsQuery>
  useLazyContributionTotalsQuery: UseLazyQuery<PendingTypeDefinition>
  useInsurancesQuery: UseQuery<InsuranceQuery>
  useLazyInsurancesQuery: UseLazyQuery<InsuranceQuery>
  useSpecialFieldsQuery: UseQuery<PendingTypeDefinition>
  useBalanceDetailsQuery: UseQuery<PendingTypeDefinition>
  useLazyMoneyInOutQuery: UseLazyQuery<MoneyInOutQuery>
  useLazyAnnualReviewsQuery: UseLazyQuery<AnnualReviewsQuery>
  useLazySuppressionsQuery: UseLazyQuery<PendingTypeDefinition>
  useBpayRefsQuery: UseQuery<PendingTypeDefinition>
  useRolloversQuery: UseQuery<RolloversQuery>
  useBeneficiariesQuery: UseQuery<BeneficiaryQuery>
  useServiceStartDateQuery: UseQuery<PendingTypeDefinition>
  useLazyPensionQuery: UseLazyQuery<PensionQuery>
  useVariancesQuery: UseQuery<PendingTypeDefinition>
  useMemberBankAccountsQuery: UseQuery<MemberBankAccountsQuery>
}

// Generated hooks
export const {
  useMemberDetailsQuery,
  useAccountBalancesQuery,
  useGetMemberIdQuery,
  useSetMemberIdMutation,
  useTransactionsQuery,
  useLazyTransactionsQuery,
  useLazyContributionTotalsQuery,
  useInsurancesQuery,
  useInsuranceSpecialFieldsQuery,
  useSpecialFieldsQuery,
  useInvestmentSwitchesQuery,
  useLazyLogoutQuery,
  useInvestmentSwitchMutation,
  useBalanceDetailsQuery,
  useAccountBalanceDatesQuery,
  useLazyAccountBalanceDateQuery,
  useLazyMoneyInOutQuery,
  useLazyAnnualReviewsQuery,
  useLazyInvestmentsQuery,
  useCancelInvestmentSwitchMutation,
  useInsuranceRatesQuery,
  useInsuranceVariationsMutation,
  usePensionVariationMutation,
  useInsuranceProfessionalMutation,
  useInsuranceProtectMutation,
  useLazySuppressionsQuery,
  useBpayRefsQuery,
  useAiaSamlQuery,
  useRolloversQuery,
  useLazyWorkflowFundsQuery,
  useRolloversSwitchMutation,
  useMemberDocumentsQuery,
  useBeneficiariesQuery,
  useUpdateDetailsMutation,
  useCountriesQuery,
  useLazyAddressSearchQuery,
  useLazyLogoutSyncQuery,
  useServiceStartDateQuery,
  useLazyPensionQuery,
  useMemberBankAccountsQuery,
  useVariancesQuery,
  useSubmitFeedbackMutation,
  useSubmitNoticeOfRetirementMutation,
  useSubmitContributionMutation,
  useSubmitInsuranceProtectMutation,
  useSubmitIlbaReceiptMutation,
  useUpdateApplicationDataMutation,
  useRemoveApplicationDataMutation,
  useApplicationDataQuery,
  useApplicationDataByIdQuery,
  useLazyApplicationDataByIdQuery,
  useLazyInsurancesQuery,
  useCreateStoredDocumentMutation,
  useRemoveStoredDocumentMutation,
  useApplicationPensionMutation,
  useStoredDocumentsQuery,
  useAddBankAccountMutation,
  useSendBankAccountReceiptMutation,
  useCloseBankAccountMutation,
  useChangeBankAccountMutation,
  useGetIlbaInclusionQuery,
} = linkApi as LinkApi
